import * as PostProcessing from 'postprocessing'
import { BlendFunction, CopyPass, Selection } from 'postprocessing'
import * as THREE from 'three'

import { N8AOPostPass } from './n8ao/main/N8AO.js'
import { isMobileOrTablet } from '../../../../trillion-common/src/utils/device-utils'
import { IGem } from '../../../../trillion-common/src/gems/IGem'
import { gems } from '../gemsConfigs'
import { PosteffectsConfigDto } from '../../../../trillion-common/src/dto/posteffects-config.dto'

export interface IPostEffects {
  bloomEffect: PostProcessing.BloomEffect
  bloomEffectSelective: PostProcessing.SelectiveBloomEffect
  depthDownsamplingPass: PostProcessing.DepthDownsamplingPass
  depthOfFieldEffect: PostProcessing.DepthOfFieldEffect
  effectPass: PostProcessing.EffectPass
  ssaoEffect: PostProcessing.SSAOEffect | undefined
  n8aoPass: N8AOPostPass
  smaaEffect: PostProcessing.SMAAEffect
  textureEffect: PostProcessing.TextureEffect
  toneMappingEffect: PostProcessing.ToneMappingEffect
  setupSelectiveBloom: (model: THREE.Group | THREE.Object3D) => void
}

export interface IBloomEffectsConfig {
  general: PostProcessing.BloomEffectOptions
  selective: PostProcessing.BloomEffectOptions
}

export type BloomRadius = (PostProcessing.BloomEffect | PostProcessing.SelectiveBloomEffect) & {
  mipmapBlurPass: { radius: number }
}

export function setupPostprocessing(
  composer: PostProcessing.EffectComposer,
  renderer: THREE.WebGLRenderer,
  scene: THREE.Scene,
  camera: THREE.PerspectiveCamera | THREE.OrthographicCamera,
  posteffectsConfig?: PosteffectsConfigDto,
  setupInWidget?: boolean,
): IPostEffects {
  const renderPass = new PostProcessing.RenderPass(scene, camera)
  const normalPass = new PostProcessing.NormalPass(scene, camera)
  const n8aoPass = new N8AOPostPass(scene, camera, window.innerWidth, window.innerHeight)

  const depthDownsamplingPass = new PostProcessing.DepthDownsamplingPass({
    normalBuffer: normalPass.texture,
    resolutionScale: 0.5,
  })

  // Depth of field effect
  const depthOfFieldConfig = posteffectsConfig?.depthOfField
  const depthOfFieldEffect = new PostProcessing.DepthOfFieldEffect(camera, {
    blendFunction: depthOfFieldConfig?.blendFunction ?? PostProcessing.BlendFunction.SKIP,
    bokehScale: depthOfFieldConfig?.bokehScale ?? 1.0,
    focusDistance: depthOfFieldConfig?.focusDistance ?? 0.003,
    focalLength: depthOfFieldConfig?.focalLength ?? 0.001,
    focusRange: depthOfFieldConfig?.focusRange ?? 0.001,
    worldFocusDistance: depthOfFieldConfig?.worldFocusDistance ?? 7,
    worldFocusRange: depthOfFieldConfig?.worldFocusRange ?? 2,
    resolutionScale: depthOfFieldConfig?.resolutionScale ?? 0.5,
  })

  // SSAO Effect
  const ssaoEffect = undefined
  /*
  const ssaoConfig = posteffectsConfig?.ssao
  const ssaoEffect = new PostProcessing.SSAOEffect(camera, depthDownsamplingPass.texture, {
    blendFunction: ssaoConfig?.blendFunction ?? PostProcessing.BlendFunction.SKIP, // disabled, set MULTIPLY to enable
    depthAwareUpsampling: true,
    normalDepthBuffer: depthDownsamplingPass.texture, // TODO: must be set separately
    samples: ssaoConfig?.samples ?? 32,
    rings: ssaoConfig?.rings ?? 14,
    worldDistanceThreshold: ssaoConfig?.worldDistanceThreshold ?? 1,
    worldDistanceFalloff: ssaoConfig?.worldDistanceFalloff ?? 0.025,
    worldProximityThreshold: ssaoConfig?.worldProximityThreshold ?? 0.01,
    worldProximityFalloff: ssaoConfig?.worldProximityFalloff ?? 0.0019,
    luminanceInfluence: ssaoConfig?.luminanceInfluence ?? 0.3,
    minRadiusScale: ssaoConfig?.minRadiusScale ?? 0.1,
    radius: ssaoConfig?.radius ?? 0.034,
    intensity: ssaoConfig?.intensity ?? 4.18,
    bias: ssaoConfig?.bias ?? 0.034,
    fade: ssaoConfig?.fade ?? 0,
    color: new THREE.Color(ssaoConfig?.color ?? 0x000011),
    resolutionScale: ssaoConfig?.resolutionScale ?? 1.0,
  })
  */

  // N8AO Pass
  const n8aoConfig = posteffectsConfig?.n8ao
  n8aoPass.configuration.aoSamples = n8aoConfig?.aoSamples ?? (isMobileOrTablet() ? 4.0 : 8.0)
  n8aoPass.configuration.denoiseSamples =
    n8aoConfig?.denoiseSamples ?? (isMobileOrTablet() ? 4.0 : 8.0)
  n8aoPass.configuration.denoiseRadius =
    n8aoConfig?.denoiseRadius ?? (isMobileOrTablet() ? 6.0 : 12.0)
  n8aoPass.configuration.aoRadius = n8aoConfig?.aoRadius ?? 5.0

  n8aoPass.configuration.distanceFalloff = n8aoConfig?.distanceFalloff ?? 1.0
  n8aoPass.configuration.gammaCorrection = n8aoConfig?.gammaCorrection ?? false
  n8aoPass.configuration.screenSpaceRadius = n8aoConfig?.screenSpaceRadius ?? false
  n8aoPass.configuration.halfRes = n8aoConfig?.halfRes ?? isMobileOrTablet()
  n8aoPass.configuration.depthAwareUpsampling = n8aoConfig?.depthAwareUpsampling ?? true
  n8aoPass.enabled = n8aoConfig?.enabled ?? false

  n8aoPass.configuration.intensity = n8aoConfig?.intensity ?? 1.5
  n8aoPass.configuration.color = new THREE.Color(n8aoConfig?.color ?? '#000000')
  n8aoPass.configuration.colorMultiply = n8aoConfig?.colorMultiply ?? true

  // Texture effect
  const textureEffect = new PostProcessing.TextureEffect(
    posteffectsConfig?.textureMapping ?? {
      blendFunction: PostProcessing.BlendFunction.SKIP,
    },
  )
  textureEffect.texture = depthDownsamplingPass.texture

  // Antialiasing
  // SMAA Effect
  const smaaConfig = posteffectsConfig?.smaa
  const smaaEffect = new PostProcessing.SMAAEffect({
    preset: smaaConfig?.smaaPreset ?? PostProcessing.SMAAPreset.HIGH,
    edgeDetectionMode: smaaConfig?.edgeDetectionMode ?? PostProcessing.EdgeDetectionMode.DEPTH,
    predicationMode: smaaConfig?.predicationMode ?? PostProcessing.PredicationMode.DEPTH,
  })
  smaaEffect.edgeDetectionMaterial.edgeDetectionThreshold = smaaConfig?.edgeThreshold ?? 0.01
  smaaEffect.edgeDetectionMaterial.predicationMode =
    smaaConfig?.predicationMode ?? PostProcessing.PredicationMode.DEPTH
  smaaEffect.edgeDetectionMaterial.predicationThreshold = smaaConfig?.predicationThreshold ?? 0.002
  smaaEffect.edgeDetectionMaterial.predicationScale = smaaConfig?.predicationScale ?? 1.0

  smaaEffect.edgeDetectionMaterial.localContrastAdaptationFactor =
    smaaConfig?.edgeContrastFactor ?? 3
  smaaEffect.blendMode.blendFunction = smaaConfig?.smaaPresetBlendMode ?? BlendFunction.SKIP

  // setup MSAA
  const maxSamples = renderer.capabilities.maxSamples
  composer.multisampling = isMobileOrTablet() ? 0 : maxSamples

  // Bloom effect
  const bloomConfig = posteffectsConfig?.bloom
  const bloomEffect = new PostProcessing.BloomEffect({
    blendFunction: bloomConfig?.blendFunction ?? PostProcessing.BlendFunction.SKIP,
    mipmapBlur: true,
    luminanceThreshold: bloomConfig?.luminanceThreshold ?? 0.9,
    luminanceSmoothing: bloomConfig?.luminanceSmoothing ?? 1.0,
    intensity: bloomConfig?.intensity ?? 0,
  })
  ;(bloomEffect as BloomRadius).mipmapBlurPass.radius = bloomConfig?.radius ?? 0.1

  // Bloom effect selective
  const bloomConfigSelective = posteffectsConfig?.bloomSelective
  const bloomEffectSelective = new PostProcessing.SelectiveBloomEffect(scene, camera, {
    blendFunction: bloomConfig?.blendFunction ?? PostProcessing.BlendFunction.SKIP,
    mipmapBlur: true,
    luminanceThreshold: bloomConfigSelective?.luminanceThreshold ?? 0.9,
    luminanceSmoothing: bloomConfigSelective?.luminanceSmoothing ?? 1.0,
    intensity: bloomConfigSelective?.intensity ?? 0,
  })
  ;(bloomEffectSelective as BloomRadius).mipmapBlurPass.radius =
    bloomConfigSelective?.radius ?? 0.001

  // Tone Mapping
  const toneMappingConfig = posteffectsConfig?.toneMapping
  const toneMappingEffect = new PostProcessing.ToneMappingEffect({
    blendFunction: toneMappingConfig?.blendFunction ?? PostProcessing.BlendFunction.SKIP,
    mode: toneMappingConfig?.mode ?? PostProcessing.ToneMappingMode.REINHARD,
    resolution: toneMappingConfig?.resolution ?? 256,
    whitePoint: toneMappingConfig?.whitePoint ?? 7.3,
    middleGrey: toneMappingConfig?.middleGrey ?? 0.95,
    minLuminance: toneMappingConfig?.minLuminance ?? 0.075,
    averageLuminance: toneMappingConfig?.averageLuminance ?? 0.16,
    adaptationRate: toneMappingConfig?.adaptationRate ?? 1.0,
  })
  renderer.toneMappingExposure = toneMappingConfig?.exposure ?? 1.6

  const posteffects = setupInWidget
    ? [bloomEffect, bloomEffectSelective, toneMappingEffect]
    : [
        smaaEffect,
        textureEffect,
        bloomEffect,
        bloomEffectSelective,
        depthOfFieldEffect,
        toneMappingEffect,
      ]

  const effectPass = new PostProcessing.EffectPass(camera, ...posteffects)

  composer.addPass(renderPass)
  composer.addPass(new CopyPass())
  composer.addPass(normalPass)
  composer.addPass(depthDownsamplingPass)
  if (!setupInWidget) {
    composer.addPass(n8aoPass)
  }
  composer.addPass(effectPass)

  function setupSelectiveBloom(model: THREE.Group | THREE.Object3D) {
    if (model && model.children) {
      const objects: THREE.Object3D[] = []
      for (const group of model.children) {
        // TODO: Сonsider the case when names of selected diamonds come in config
        gems.forEach((gem: IGem) => {
          const objectsByName = group.children.filter(
            (object) => object.name.includes(gem.name) || object.name.includes('Diamond'),
          )

          objects.push(...objectsByName)
        })

        bloomEffectSelective.selection = new Selection(objects)
      }
    }
  }

  return {
    bloomEffect,
    bloomEffectSelective,
    depthDownsamplingPass,
    depthOfFieldEffect,
    effectPass,
    textureEffect,
    ssaoEffect,
    n8aoPass,
    smaaEffect,
    toneMappingEffect,
    setupSelectiveBloom,
  }
}
